import { MODEL_SSE_URL } from "@/Environment";
import { auth } from "@/firebase/config";

// Plain messages for the usage-limit codes the server returns in "error"
const SERVER_ERROR_MESSAGES = {
  guest_limit: "You've used today's free analyses for this framework. Sign up for a free account to continue.",
  monthly_limit: "You've used this month's analyses for this framework. Your allowance resets at the start of next month.",
  daily_capacity: "The service has reached its limit for new analyses today. Please try again tomorrow.",
  monthly_capacity: "The service has reached its limit for new analyses this month. Please try again next month.",
  email_not_verified: "Please verify your email address, then sign in again.",
  network_unknown: "We couldn't identify your network. Please sign in to run an analysis.",
  limits_unavailable: "The analysis service is temporarily unavailable. Please try again later.",
  analysis_unavailable: "The analysis service is temporarily unavailable. Please try again later.",
};

/**
 * Turn a non-200 response into an Error with a readable message.
 * The body is one SSE line: data: {"error": "<code or message>", "message": "..."}
 */
function serverError(status, body) {
  let data = {};
  try {
    data = JSON.parse(body.replace(/^data:\s*/, "").trim());
  } catch {
    // Not JSON; fall back to a generic message
  }
  const message =
    SERVER_ERROR_MESSAGES[data.error] ||
    (status === 401 ? "Your sign-in has expired. Please sign in again." : null) ||
    data.message ||
    data.error ||
    `Analysis failed (HTTP ${status}). Please try again.`;
  const error = new Error(message);
  error.code = data.error || null;
  error.status = status;
  return error;
}

/**
 * Run model analysis with Server-Sent Events (SSE) for real-time progress updates
 *
 * @param {string} companyName - Name of the company
 * @param {string} fileName - PDF file name
 * @param {ArrayBuffer} pdfData - PDF file data
 * @param {Function} onProgress - Callback for progress updates (data) => void
 * @param {Function} onComplete - Callback when analysis is complete (result) => void
 * @param {Function} onError - Callback for errors (error) => void
 * @param {string} industry - Optional industry for sector-contextualized analysis
 * @param {string} framework - Framework type: "current" (48 questions) or "advanced" (61 questions)
 * @param {string} companyNumber - Optional Companies House number for disambiguation
 * @param {string} homepageUrl - Optional homepage URL for Q1 verification
 * @returns {Function} - Cleanup function to close the EventSource
 */
export async function runModelWithSSE(
  companyName,
  fileName,
  pdfData,
  onProgress,
  onComplete,
  onError,
  industry = null,
  framework = "current",
  companyNumber = null,
  homepageUrl = null
) {
  // Get the current user's ID token
  const user = auth.currentUser;
  let idToken = null;

  if (user) {
    try {
      idToken = await user.getIdToken();
    } catch (error) {
      console.error("Failed to get ID token:", error);
      onError(new Error("Authentication failed. Please sign in again."));
      return () => {}; // Return empty cleanup function
    }
  }

  // First, upload the PDF and initiate SSE connection
  const params = new URLSearchParams({
    companyName,
    pdf_ID: fileName,
  });

  // Add industry parameter if provided
  if (industry) {
    params.append('industry', industry);
  }

  if (companyNumber) {
    params.append('company_number', companyNumber);
  }

  if (homepageUrl) {
    params.append('homepage', homepageUrl);
  }

  // Add framework parameter (defaults to "current")
  params.append('framework', framework || 'current');

  const sseUrl = MODEL_SSE_URL;

  // For SSE, we need to use EventSource which doesn't support POST with body
  // So we'll use fetch with a streaming response instead
  try {
    const headers = {
      "Content-Type": "application/pdf",
    };

    if (idToken) {
      headers["Authorization"] = `Bearer ${idToken}`;
    }

    const response = await fetch(`${sseUrl}?${params.toString()}`, {
      method: "POST",
      headers,
      body: pdfData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      onError(serverError(response.status, errorText));
      return () => {};
    }

    // Check if response is SSE
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('text/event-stream')) {
      onError(new Error('Expected SSE response but got: ' + contentType));
      return () => {};
    }

    // Read the stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const readStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE messages
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || ''; // Keep incomplete message in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const jsonStr = line.substring(6); // Remove 'data: ' prefix

              try {
                const data = JSON.parse(jsonStr);

                if (data.type === 'progress') {
                  onProgress(data);
                } else if (data.type === 'complete' || data.type === 'result') {
                  onComplete(data.result || data.data);
                  break;
                } else if (data.type === 'error' || data.error) {
                  onError(new Error(data.message || data.error || 'Analysis failed'));
                  break;
                }
              } catch (parseError) {
                console.error('Failed to parse SSE message:', jsonStr, parseError);
              }
            }
          }
        }
      } catch (streamError) {
        console.error('Stream reading error:', streamError);
        onError(streamError);
      }
    };

    // Start reading the stream
    readStream();

    // Return cleanup function
    return () => {
      reader.cancel();
    };

  } catch (fetchError) {
    console.error('Fetch error:', fetchError);
    onError(fetchError);
    return () => {};
  }
}
